import { ReactNode } from 'react';
import { currency } from '../formatters';

// Indemnity payout waterfall — the grid rows shared by every indemnity-capable
// per-cell payout tooltip. Indemnity pays a fixed cash benefit regardless of
// actual cost, so the reimbursement-style "eligible cost → over-cap / stranded-
// day buckets" breakdown is the wrong frame: the cash (plus any drained surplus
// reserve) simply gets applied to cost, and cash above the cost banks as
// surplus. Modeling it as a cash waterfall keeps every line reconciling — unlike
// the day-bucket breakdown, where on a partial benefit-period boundary row the
// indemnity cash can exceed the period-clamped eligible cost and a sub-bucket
// ends up larger than the out-of-pocket subtotal it itemizes.
//
// All figures come straight off the engine's payout deltas:
//   indemnityPayout  — gross cash paid this row
//   ltcCoveragePaid  — cash applied directly to cost (capped at eligible cost)
//   ltcPayoutToCost  — total applied to cost (direct + surplus reserve drained)
// Per row exactly one of the two surplus movements is non-zero (the reserve
// either drains into a shortfall or banks the excess — never both), so the two
// conditional lines are mutually exclusive:
//   surplus applied = ltcPayoutToCost − ltcCoveragePaid   (reserve → cost)
//   banked surplus  = indemnityPayout − ltcCoveragePaid   (cash above cost → reserve)
// and net out-of-pocket = effectiveCareCost − ltcPayoutToCost (= `ineligibleCost`).
//
// `cashNote` is an optional small annotation on the "Indemnity cash paid" line,
// used by STC to explain a pro-rated cash benefit (the monthly cap is scaled to
// the payable days remaining in the lifetime benefit period — e.g. a boundary
// month pays cap × 15/30 when only 15 benefit-period days are left).
export function indemnityPayoutWaterfall(month: MonthlyEntry, cashNote?: string): ReactNode {
  const cost = month.facts.effectiveCareCost;
  const indemnityCash = month.deltas.payout.indemnityPayout;
  const directlyApplied = month.deltas.payout.ltcCoveragePaid;
  const appliedToCost = month.deltas.payout.ltcPayoutToCost;
  const surplusApplied = Math.max(appliedToCost - directlyApplied, 0);
  const surplusBanked = Math.max(indemnityCash - directlyApplied, 0);
  const netOop = Math.max(cost - appliedToCost, 0);

  return (
    <>
      <span className="text-gray-300">Care cost</span>
      <span className="font-semibold tabular-nums">{currency(cost)}</span>
      <span className="text-gray-300">
        Indemnity cash paid
        {cashNote ? <span className="text-gray-500"> · {cashNote}</span> : null}
      </span>
      <span className="font-semibold tabular-nums">{currency(indemnityCash)}</span>
      {surplusApplied > 0 && (
        <>
          <span className="pl-2 text-gray-400">+ surplus reserve applied</span>
          <span className="tabular-nums text-emerald-300">+{currency(surplusApplied)}</span>
        </>
      )}
      <span className="text-gray-300">Applied to cost</span>
      <span className="font-semibold tabular-nums">{currency(appliedToCost)}</span>
      {surplusBanked > 0 && (
        <>
          <span className="text-gray-300">Banked as surplus</span>
          <span className="font-semibold tabular-nums text-emerald-300">
            +{currency(surplusBanked)}
          </span>
        </>
      )}
      <span className="text-gray-300">Net out of pocket</span>
      <span className="font-semibold tabular-nums text-rose-300">{currency(netOop)}</span>
    </>
  );
}
